import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

export type PerfStats = {
  hardwareConcurrency?: number;
  deviceMemory?: number; // GB (coarse)
  network?: {
    downlink?: number; // Mbps
    effectiveType?: string; // '4g' | '3g' | ...
    rtt?: number; // ms
  };
  jsHeap?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
  };
  fps?: number;
  longTasks?: { count: number; totalBlockingTime: number }; // ms over 50ms budgets
  workerBenchMs?: number; // lower is better
  webgpu?: boolean;
  webglRenderer?: string; // may be empty if restricted
};

export default function usePerformanceStats(pollMs = 1000): PerfStats {
    const [stats, setStats] = useState<PerfStats>({});
    const rafRef = useRef<number | null>(null);
    const frameCountRef = useRef(0);
    const lastTickRef = useRef<number | null>(null);
    const fpsRef = useRef<number>(0);

    // Capabilities (static-ish)
    useEffect(() => {
        const base: PerfStats = {
            hardwareConcurrency: navigator.hardwareConcurrency,
            deviceMemory: (navigator as any).deviceMemory,
            webgpu: !!(navigator as any).gpu,
        };

        // Network Information API
        const conn = (navigator as any).connection;
        if (conn) {
            base.network = {
                downlink: conn.downlink,
                effectiveType: conn.effectiveType,
                rtt: conn.rtt,
            };
            const onConnChange = (): void => {
                setStats((s) => ({
                    ...s,
                    network: {
                        downlink: conn.downlink,
                        effectiveType: conn.effectiveType,
                        rtt: conn.rtt,
                    },
                }));
            };
            conn.addEventListener?.('change', onConnChange);
            return () => conn.removeEventListener?.('change', onConnChange);
        }

        setStats((s) => ({ ...base, ...s }));
    }, []);

    // WebGL renderer (best-effort; may be blocked)
    useEffect(() => {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl) {
                const ext = (gl as any).getExtension('WEBGL_debug_renderer_info');
                const renderer = ext
                    ? (gl as any).getParameter(ext.UNMASKED_RENDERER_WEBGL)
                    : '';
                if (renderer) {
                    setStats((s) => ({ ...s, webglRenderer: String(renderer) }));
                }
            }
        } catch {
            /* ignore */
        }
    }, []);

    // JS heap (Chrome-only)
    useEffect(() => {
        const t = setInterval(() => {
            const mem = (performance as any).memory;
            if (mem) {
                setStats((s) => ({
                    ...s,
                    jsHeap: {
                        edJSHeapSize: mem.usedJSHeapSize,
                        totalJSHeapSize: mem.totalJSHeapSize,
                    },
                }));
            }
        }, pollMs);
        return () => clearInterval(t);
    }, [pollMs]);

    // FPS (ema)
    useEffect(() => {
        const loop = (now: number): void => {
            frameCountRef.current += 1;
            if (lastTickRef.current == null) lastTickRef.current = now;
            const dt = now - lastTickRef.current; // ms
            if (dt >= 1000) {
                const fps = (frameCountRef.current * 1000) / dt;
                // Exponential moving average for stability
                fpsRef.current = fpsRef.current ? fpsRef.current * 0.6 + fps * 0.4 : fps;
                setStats((s) => ({ ...s, fps: Math.round(fpsRef.current) }));
                frameCountRef.current = 0;
                lastTickRef.current = now;
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    // Long Tasks
    useEffect(() => {
        if (!('PerformanceObserver' in window)) return;
        let totalBlockingTime = 0;
        let count = 0;
        const observer = new PerformanceObserver((list) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const entry of list.getEntries() as PerformanceEntryList & unknown) {
                // Long task: duration > 50ms
                const blocking = Math.max(0, entry.duration - 50);
                totalBlockingTime += blocking;
                count += 1;
            }
            setStats((s) => ({
                ...s,
                longTasks: { count, totalBlockingTime: Math.round(totalBlockingTime) },
            }));
        });
        try {
            observer.observe({ type: 'longtask', buffered: true } as any);
        } catch {
            /* some browsers require 'entryTypes' */
            try {
                observer.observe({ entryTypes: ['longtask'] as any });
            } catch {
            /* ignore */
            }
        }
        return () => observer.disconnect();
    }, []);

    // Tiny CPU worker benchmark (non-blocking)
    const workerUrl = useMemo(() => {
        const src = `
            self.onmessage = () => {
            const start = performance.now();
            // Compute-bound loop (small but predictable)
            let acc = 0;
            for (let i = 0; i < 5_000_000; i++) {
                acc = (acc * 1664525 + 1013904223) | 0; // LCG-ish
            }
            const end = performance.now();
            self.postMessage({ ms: end - start, acc });
            };
        `;
        const blob = new Blob([src], { type: 'application/javascript' });
        return URL.createObjectURL(blob);
    }, []);

    useEffect(() => {
        const w = new Worker(workerUrl);
        w.onmessage = (e) => {
            const ms = e.data?.ms as number;
            setStats((s) => ({ ...s, workerBenchMs: Math.round(ms) }));
            w.terminate();
            URL.revokeObjectURL(workerUrl);
        };
        // Kick it off
        w.postMessage(null);
        return () => {
            // eslint-disable-next-line no-empty
            try { w.terminate(); } catch {}
            URL.revokeObjectURL(workerUrl);
        };
    }, [workerUrl]);

    return stats;
}
